/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import {
    object, shape, string, arrayOf,
} from 'prop-types';
import { compose } from 'recompose';
import { withRouter } from 'react-router';
import Grid from '@material-ui/core/Grid';
import ReactMarkdown from 'react-markdown';
import { useUIDSeed } from 'react-uid';
import ResponsiveImage from '../../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import LinkOrATag from '../../GraphqlCommonComponents/LinkOrATag/LinkOrATag';
import { getFeatureFlags, getCurrentBrandId } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getOperatingSystem } from '../../../../../state/ducks/App/ducks/Config/Config-Helpers';
import { determineChatlink } from '../../../MultiBrandChatController/Global_chat_helpers';
import triggerPopChatEvent from '../../../MultiBrandChatController/AigoChatWidgetWrapperV2/popOpenChat';

const styles = (theme) => ({
    heroContainer: {
        position: 'relative',
        '& a': {
            textDecoration: 'none',
            color: theme.palette.common?.black,
            margin: '0.625rem 0.938rem 0',
            display: 'block',
            [theme.breakpoints.down('sm')]: {
                margin: '0.625rem 0.313rem',
            },
        },
    },
    heroImage: {
        width: '100%',
        display: 'block',
        [theme.breakpoints.down('xs')]: {
            minHeight: '250px',
        },
        [theme.breakpoints.down('md')]: {
            minHeight: '250px',
        },
    },
    heroText: {
        zIndex: '9',
        color: theme.palette.common?.black,
        top: '20%',
        right: '50%',
        width: '60%',
        position: 'absolute',
        transform: 'translateX(50%)',
        textAlign: 'center',
        [theme.breakpoints.down('xs')]: {
            width: '100%',
            top: '5%',
        },
        [theme.breakpoints.down('sm')]: {
            zIndex: 0,
            top: '5%',
        },
        '& h1 , & p': {
            margin: 0,
            padding: 0,
            fontSize: '2.25rem',
            [theme.breakpoints.down('xs')]: {
                fontSize: '1rem',
            },
        },
        '& h1,h2,h3,h4,h5,h6': {
            margin: 0,
            padding: 0,
        },
        '& h2': {
            fontSize: '0.875rem',
        },
    },
    heroButton: {
        background: theme.palette.common?.white,
        display: 'flex',
        boxShadow: '0 3px 3px rgba(0, 0, 0, 0.2)',
        padding: '20px 15px',
        [theme.breakpoints.down('sm')]: {
            padding: '20px 5px',
        },
        '& img': {
            width: '40px',
            height: '40px',
            [theme.breakpoints.down('xs')]: {
                width: '30px',
                height: '30px',
            },
        },
        '& div': {
            position: 'relative',
            width: '100%',
            display: 'flex',
            alignItems: 'flex-start',
            marginLeft: '0.438rem',
            flexDirection: 'column',
            justifyContent: 'center',
        },
        '& p': {
            fontSize: '0.875rem',
            [theme.breakpoints.down('xs')]: {
                fontSize: '0.75rem',
            },
        },
    },
    arrow: {
        position: 'absolute',
        right: '0',
        color: theme.palette.cms?.primary || theme.palette.colorPrimary,
        fontSize: '1.125rem',
        [theme.breakpoints.down('xs')]: {
            bottom: 0,
        },
    },
    heroButtonContainer: {
        width: '100%',
    },
    customerServiceContainer: {
        '& $arrow': {
            top: '4px',
            lineHeight: '14px',
        },
        '& $heroButton': {
            '& p': {
                width: '100%',
                alignItems: 'center',
                display: 'flex',
                position: 'relative',
            },
        },
        [theme.breakpoints.down(1366)]: {
            '& $heroText': {
                width: '70%',
                margin: '0 auto',
            },
        },
        [theme.breakpoints.down('md')]: {
            '& $heroImage': {
                height: '250px',
            },
            '& $heroButtonContainer': {
                width: '80%',
                margin: '0 auto',
            },
            '& $heroText': {
                top: '5%',
                right: '0',
                transform: 'none',
                width: '100%',
                '& h1 , & p': {
                    fontSize: '1.875rem',
                },
                '& $heroButton': {
                    minHeight: '95px',
                    '& p': {
                        textAlign: 'left',
                        fontSize: '0.75rem',
                        paddingRight: '0.625rem',
                    },
                },
            },
        },
        [theme.breakpoints.down(767)]: {
            '& $heroButtonContainer': {
                width: '90%',
            },
        },
        [theme.breakpoints.down('xs')]: {
            '& $heroText': {
                '& h1 , & p': {
                    fontSize: '1rem',
                },
                '& $heroButton': {
                    minHeight: 'auto',
                    margin: '0.625rem 0.313rem',
                },
            },
        },
    },
});

const HeroBlock = ({ data, classes, history }) => {
    const seed = useUIDSeed();
    const currentPath = history?.location?.pathname;
    const operatingSystem = getOperatingSystem().toLowerCase();
    const currentBrand = useSelector((state) => getCurrentBrandId(state));
    const customerData = useSelector((state) => state?.member);
    const featureFlags = useSelector((state) => getFeatureFlags(state));
    const isMobileChatEnabled = ((featureFlags['is-gbm-in-pwa-enabled'] && operatingSystem === 'android') || (featureFlags['is-abc-in-pwa-enabled'] && operatingSystem === 'ios'));
    const {
        desktop_image, copy, hero_buttons, header = {},
        copy_alignment = '',
        copy_font_size = '',
        copy_color = {},
    } = data;
    const heroTitle = data?.hero_buttons?.[1]?.link?.title;
    const heading = header?.title;
    const loadCssUrlBase = () => {
        switch (currentPath) {
            case '/customer-service':
                return classes.customerServiceContainer;
            default:
                return null;
        }
    };

    const useNewDimensionsIfAvailable = (fontSize, color, alignItem) => {
        const bannerStyle = {};
        if (fontSize) {
            bannerStyle.fontSize = `${fontSize}em`;
        }
        if (color) {
            bannerStyle.color = color;
        }
        if (alignItem) {
            bannerStyle.textAlign = alignItem;
        }
        return bannerStyle;
    };

    const desktopPopChat = () => {
        if (operatingSystem !== 'android' && operatingSystem !== 'ios') {
            triggerPopChatEvent({});
        }
    };
    return (
        <>
            <div className={`${classes.heroContainer} ${loadCssUrlBase()}`}>
                <ResponsiveImage className={classes.heroImage} path={desktop_image?.url} alt="hero" params={{}} />
                <div className={classes.heroText}>
                    {heading && (
                        <h1
                            style={useNewDimensionsIfAvailable(
                                header?.font_size,
                                header?.color?.color,
                                header?.copy_alignment,
                            )}
                        > {heading}
                        </h1>
                    )}
                    <div
                        style={useNewDimensionsIfAvailable(
                            copy_font_size,
                            copy_color?.color,
                            copy_alignment,
                        )}
                    >
                        <ReactMarkdown
                            source={copy}
                            skipHtml
                        />
                    </div>
                    <Grid container className={classes.heroButtonContainer}>
                        {hero_buttons?.length > 0 && hero_buttons.map((button, i) => {
                            const { link: { title = '', href = '' } = {}, image: { url = '' } = {}, sub_copy = '' } = button;
                            return (
                                title && title.toLowerCase()?.includes('chat')
                                    ? (
                                        (featureFlags['is-cs-page-chat-enabled'] && (!['ios', 'android'].includes(operatingSystem) || isMobileChatEnabled))
                                        && (
                                            <Grid key={seed(button)} item xs={12} sm={6} md={6} lg={6}>
                                                <LinkOrATag
                                                    id={`heroButton-${i}`}
                                                    href={determineChatlink(operatingSystem, currentBrand, featureFlags, customerData) || currentPath}
                                                    ariaLabel={heroTitle}
                                                >
                                                    <div
                                                        className={classes.heroButton}
                                                        onClick={() => desktopPopChat()}
                                                        onKeyDown={() => desktopPopChat()}
                                                        role="button"
                                                        tabIndex="0"
                                                    >
                                                        <ResponsiveImage path={url} alt="hero icon" />
                                                        <div>
                                                            <strong>{title}</strong>
                                                            {!sub_copy && <strong className={classes.arrow}>&#x3E;</strong>}
                                                            {sub_copy && <p>{sub_copy}<strong className={classes.arrow}>&#x3E;</strong></p>}
                                                        </div>
                                                    </div>
                                                </LinkOrATag>
                                            </Grid>
                                        )
                                    )
                                    : (
                                        <Grid key={seed(button)} item xs={12} sm={6} md={6} lg={6}>
                                            <LinkOrATag id={`heroButton-${i}`} href={href}>
                                                <div className={classes.heroButton}>
                                                    <ResponsiveImage path={url} alt="hero icon" />
                                                    <div>
                                                        <strong>{title}</strong>
                                                        {!sub_copy && <strong className={classes.arrow}>&#x3E;</strong>}
                                                        {sub_copy && <p>{sub_copy}<strong className={classes.arrow}>&#x3E;</strong></p>}
                                                    </div>
                                                </div>
                                            </LinkOrATag>
                                        </Grid>
                                    )
                            );
                        })}
                    </Grid>
                </div>
            </div>
        </>
    );
};

HeroBlock.propTypes = {
    classes: object.isRequired,
    data: shape({
        copy: string,
        copy_alignment: string,
        copy_font_size: string,
        copy_color: shape({
            color: string,
        }),
        header: shape({
            title: string,
            copy_alignment: string,
            copy_font_size: string,
            copy_color: shape({
                color: string,
            }),
        }),
        desktop_image: shape({
            url: string.isRequired,
        }).isRequired,
        hero_buttons: arrayOf(shape({
            link: shape({
                title: string.isRequired,
                href: string.isRequired,
            }).isRequired,
            image: shape({
                url: string.isRequired,
            }).isRequired,
            sub_copy: string.isRequired,
        })),
    }),
    history: object.isRequired,
};
HeroBlock.defaultProps = {
    data: {},
};
const enhance = compose(
    withRouter,
);
export default (enhance(withStyles(styles)(HeroBlock)));
